import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  Select,
  FormControl,
  FormLabel,
  Divider,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useColorModeValue,
  useDisclosure,
  useToast,
  Flex,
  SimpleGrid,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiTrendingUp,
  FiTrendingDown,
  FiUploadCloud,
  FiPercent,
  FiPieChart,
} from "react-icons/fi";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useFunds } from "../../hooks/useFunds";
import {
  auth,
  easyPurchase,
  banks,
  files,
  paymentCards,
  paymentSlips,
  requests,
  tadbir,
} from "../../services/api";
import { getFundLogoSrc } from "./fundCardUtils";
import {
  formatPersianDayValue,
  toGregorianIsoDate,
} from "../../utils/persianDate";
import {
  PersianDatePicker,
  PersianDayValue,
} from "../common/PersianDatePicker";
import { SejamSyncRequiredSection } from "../profile/SejamSyncRequiredSection";
import {
  getUserIdentifier,
  getUserIdentifierLabel,
} from "../../utils/userIdentity";
import { InvestmentSkeleton } from "./investment/InvestmentSkeleton";
import { InvestmentHeader } from "./investment/InvestmentHeader";
import { InfoRow } from "./investment/InfoRow";
import { InvestmentAgreement } from "./investment/InvestmentAgreement";
import {
  CalculatorInputMode,
  FundCalculatorModal,
  ReinvestModal,
} from "../dashboard/FundOrderModals";
import { ProfileFundCardEntry } from "../dashboard/ProfileFundCard";
import {
  getFundRedemptionNavLabel,
  getFundSubscriptionNavLabel,
  isFundInvestmentEnabled,
} from "./fundCardUtils";
import {
  formatNumber,
  floorWholeUnits,
  getWageSetting,
  isSepahBrokerFund,
  resolveEasyPurchaseFundKey,
} from "./investment/helpers";
import {
  CardLookup,
  InputMode,
  InvestmentType,
  PaymentSlipForm,
  PaymentMethod,
  Step,
} from "./investment/types";

const MotionBox = motion(Box);

const initialPaymentSlipForm: PaymentSlipForm = {
  sourceAccountNumber: "",
  destinationAccountNumber: "",
  sourceBankCode: "",
  sourceBankName: "",
  sourceBankId: "",
  amountRial: "",
};

const INVESTMENT_STEPS = [{ label: "جزئیات سفارش" }, { label: "تأیید و ثبت" }];

const MIN_SEPAH_ISSUANCE_AMOUNT_RIAL = 5000000;
const MIN_GATEWAY_ISSUANCE_AMOUNT_RIAL = 1500000;
const MAX_GATEWAY_ISSUANCE_AMOUNT_RIAL = 4000000000;
const CALCULATOR_FUND_CODES = new Set(["11343", "11383", "11222"]);

const formatGiftCardNumber = (value = "") =>
  String(value)
    .replace(/[^\d]/g, "")
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, "$1-");

const calculateVariableWage = (
  amountRial: number,
  wageRate: number,
  maxVariableWage: number,
) => {
  if (!Number.isFinite(amountRial) || amountRial <= 0) {
    return 0;
  }

  const rawVariableWage = Math.round(amountRial * (wageRate / 100));
  return Math.min(
    rawVariableWage,
    maxVariableWage > 0 ? maxVariableWage : Number.MAX_SAFE_INTEGER,
  );
};

const calculateIssuanceWage = (
  amountRial: number,
  fixedWage: number,
  wageRate: number,
  maxVariableWage: number,
) =>
  amountRial > 0
    ? fixedWage +
      calculateVariableWage(amountRial, wageRate, maxVariableWage)
    : 0;

const calculateCardIssuanceBreakdown = ({
  cardAmountRial,
  unitPrice,
  fixedWage,
  wageRate,
  maxVariableWage,
}: {
  cardAmountRial: number;
  unitPrice: number;
  fixedWage: number;
  wageRate: number;
  maxVariableWage: number;
}) => {
  if (
    !Number.isFinite(cardAmountRial) ||
    cardAmountRial <= 0 ||
    !Number.isFinite(unitPrice) ||
    unitPrice <= 0
  ) {
    return {
      units: 0,
      investmentAmountRial: 0,
      wageAmountRial: 0,
      payableAmountRial: Math.max(0, Math.round(cardAmountRial || 0)),
    };
  }

  let low = 0;
  let high = Math.floor(cardAmountRial / unitPrice);
  let bestUnits = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const investmentAmountRial = mid * unitPrice;
    const wageAmountRial = calculateIssuanceWage(
      investmentAmountRial,
      fixedWage,
      wageRate,
      maxVariableWage,
    );
    const payableAmountRial = investmentAmountRial + wageAmountRial;

    if (payableAmountRial <= cardAmountRial) {
      bestUnits = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  const investmentAmountRial = bestUnits * unitPrice;
  const wageAmountRial = calculateIssuanceWage(
    investmentAmountRial,
    fixedWage,
    wageRate,
    maxVariableWage,
  );

  return {
    units: bestUnits,
    investmentAmountRial,
    wageAmountRial,
    payableAmountRial: Math.round(cardAmountRial),
  };
};

const resolveSubscriptionValidationMessage = ({
  fund,
  units,
  amountRial,
  paymentMethod,
  isSepahFund,
}: {
  fund:
    | {
        minimumCapitalUnit?: number;
        maximumCapitalUnit?: number;
        remainedUnit?: number;
      }
    | undefined;
  units: number;
  amountRial: number;
  paymentMethod: PaymentMethod;
  isSepahFund: boolean;
}) => {
  if (paymentMethod === "slip") {
    if (!Number.isSafeInteger(amountRial) || amountRial <= 0) {
      return "مبلغ فیش انتقال باید معتبر باشد.";
    }

    return "";
  }

  if (!Number.isSafeInteger(amountRial) || amountRial < 1000) {
    return "مبلغ نهایی صدور باید معتبر باشد.";
  }

  if (paymentMethod === "gateway" || paymentMethod === "card") {
    const minimumAmountRial = isSepahFund
      ? MIN_SEPAH_ISSUANCE_AMOUNT_RIAL
      : MIN_GATEWAY_ISSUANCE_AMOUNT_RIAL;
    const maximumAmountRial = isSepahFund
      ? null
      : MAX_GATEWAY_ISSUANCE_AMOUNT_RIAL;

    if (amountRial < minimumAmountRial) {
      return `حداقل مبلغ مجاز برای صدور ${formatNumber(minimumAmountRial)} ریال است.`;
    }

    if (maximumAmountRial !== null && amountRial > maximumAmountRial) {
      return `حداکثر مبلغ مجاز برای صدور ${formatNumber(maximumAmountRial)} ریال است.`;
    }
  }

  const remainedUnits = Math.max(0, Number(fund?.remainedUnit || 0));

  if (!Number.isFinite(units)) {
    return "تعداد واحد صدور باید معتبر باشد.";
  }

  if (remainedUnits <= 0) {
    return "در حال حاضر واحد قابل صدور برای این صندوق باقی نمانده است.";
  }

  if (units > remainedUnits) {
    return `در حال حاضر حداکثر ${formatNumber(remainedUnits)} واحد برای صدور این صندوق باقی مانده است.`;
  }

  if (units <= 0) {
    return "تعداد واحد صدور باید معتبر باشد.";
  }

  return "";
};

// ─── Component ─────────────────────────────────────────────

export const Investment = () => {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const toast = useToast();
  const reinvestModal = useDisclosure();
  const calculatorModal = useDisclosure();
  const { data, isLoading } = useFunds();
  const fund = data?.items.find((f) => f.fundRegisterCode === fundId);
  const {
    data: currentUserResponse,
    isLoading: isUserLoading,
    refetch: refetchCurrentUser,
  } = useQuery({
    queryKey: ["auth", "me", "investment"],
    queryFn: () => auth.getMe(),
  });
  const currentUser = currentUserResponse?.result;
  const identityUser = currentUser || user;
  const userIdentifier = getUserIdentifier(identityUser);
  const requiresSejamSync = !currentUser?.hasSejamRecord;
  const requiresSepahRegistration =
    isSepahBrokerFund(fundId) && currentUser?.isSepahVerified === false;
  const sepahRegisterUrl = currentUser?.sepahRegisterUrl || "";

  const [step, setStep] = useState<Step>(1);
  const [investmentType, setInvestmentType] =
    useState<InvestmentType>("subscription");
  const [inputMode, setInputMode] = useState<InputMode>("amount");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("gateway");
  const [amountStr, setAmountStr] = useState("");
  const [unitStr, setUnitStr] = useState("");
  const [calculatorAmount, setCalculatorAmount] = useState("");
  const [calculatorInputMode, setCalculatorInputMode] =
    useState<CalculatorInputMode>("amount");
  const [cardNumber, setCardNumber] = useState("");
  const [cardLookup, setCardLookup] = useState<CardLookup | null>(null);
  const [cardLookupError, setCardLookupError] = useState("");
  const [isCardLookupLoading, setIsCardLookupLoading] = useState(false);
  const [paymentSlipForm, setPaymentSlipForm] = useState<PaymentSlipForm>(
    initialPaymentSlipForm,
  );
  const [slipImage, setSlipImage] = useState<File | null>(null);
  const [transferedAt, setTransferedAt] = useState<PersianDayValue>(null);
  const [bankOptions, setBankOptions] = useState<any[]>([]);
  const [selectedBankObjectId, setSelectedBankObjectId] = useState("");
  const [isBanksLoading, setIsBanksLoading] = useState(false);
  const [banksError, setBanksError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Color tokens ──
  const sectionBg = useColorModeValue("gray.50", "dark.900");
  const cardBg = useColorModeValue("white", "dark.50");
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const heroBgGradient = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249,168,166,0.25), transparent 55%), radial-gradient(circle at 10% 80%, rgba(249,168,166,0.2), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173,11,6,0.12), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173,11,6,0.12), transparent 50%)",
  );
  const subscriptionActiveBg = useColorModeValue("green.500", "green.400");
  const subscriptionActiveSoftBg = useColorModeValue("green.50", "green.900");
  const subscriptionActiveSoftText = useColorModeValue("green.700", "green.200");
  const subscriptionActiveBorder = useColorModeValue("green.500", "green.400");
  const redemptionActiveBg = useColorModeValue("brand.500", "brand.400");
  const redemptionActiveSoftBg = useColorModeValue("brand.50", "rgba(173,11,6,0.15)");
  const redemptionActiveSoftText = useColorModeValue("brand.600", "brand.300");
  const redemptionActiveBorder = useColorModeValue("brand.500", "brand.400");
  const actionColorScheme =
    investmentType === "subscription" ? "green" : "brand";
  const actionAccentColor =
    investmentType === "subscription" ? "green.500" : "brand.500";
  const typeActiveBg =
    investmentType === "subscription"
      ? subscriptionActiveBg
      : redemptionActiveBg;
  const typeActiveText = "white";
  const typeInactiveText = useColorModeValue("gray.600", "gray.300");
  const typeActiveBorder =
    investmentType === "subscription"
      ? subscriptionActiveBorder
      : redemptionActiveBorder;
  const calcBg =
    investmentType === "subscription"
      ? subscriptionActiveSoftBg
      : redemptionActiveSoftBg;
  const calcDivider = useColorModeValue(
    investmentType === "subscription" ? "green.100" : "brand.100",
    investmentType === "subscription"
      ? "rgba(34,197,94,0.25)"
      : "rgba(173,11,6,0.2)",
  );
  const modeActiveBg =
    investmentType === "subscription"
      ? subscriptionActiveSoftBg
      : redemptionActiveSoftBg;
  const modeActiveText =
    investmentType === "subscription"
      ? subscriptionActiveSoftText
      : redemptionActiveSoftText;
  const modeHoverBg = useColorModeValue("gray.100", "gray.700");
  const stepDoneBg = useColorModeValue("green.500", "green.400");
  const stepActiveBg = useColorModeValue("brand.500", "brand.400");
  const stepInactiveBg = useColorModeValue("gray.200", "gray.600");

  // ── Derived ──
  const subscriptionNav = fund?.nav?.subscriptionNav ?? 0;
  const cancelNav = fund?.nav?.cancelNav ?? 0;
  const fundLogoSrc = fund ? getFundLogoSrc(fund.fundRegisterCode) : null;
  const isSepahFund = isSepahBrokerFund(fund?.fundRegisterCode);
  const canUseCalculator = CALCULATOR_FUND_CODES.has(
    String(fund?.fundRegisterCode || fundId || "").trim(),
  );
  const isInvestmentDisabled = !isFundInvestmentEnabled(fundId);
  const activeNav =
    investmentType === "subscription" ? subscriptionNav : cancelNav;
  const {
    data: investorProfile,
    isLoading: isInvestorProfileLoading,
    refetch: refetchInvestorProfile,
  } = useQuery({
      queryKey: ["tadbirProfile", userIdentifier],
      queryFn: async () => {
        const response = await tadbir.getProfile();
        return response.profile;
      },
      enabled: Boolean(userIdentifier) && !requiresSejamSync,
      refetchOnWindowFocus: false,
    });
  const investorFund = useMemo(() => {
    const fundIdentifiers = new Set(
      [fundId, fund?.fundRegisterCode, fund?.nav?.seoRegisterNo]
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    );

    return investorProfile?.funds?.find((item) =>
      fundIdentifiers.has(String(item.fundRegisterCode || "").trim()),
    );
  }, [
    fund?.fundRegisterCode,
    fund?.nav?.seoRegisterNo,
    fundId,
    investorProfile?.funds,
  ]);
  const investorTotalUnits = Number(investorFund?.stats?.totalUnits || 0);
  const investorTotalAsset = Number(investorFund?.stats?.totalAsset || 0);
  const canManageDividend = ["11383", "11343"].includes(
    String(fund?.fundRegisterCode || fundId || "").trim(),
  );

  const amount = useMemo(() => {
    const n = Number(amountStr.replace(/,/g, ""));
    return isNaN(n) ? 0 : n;
  }, [amountStr]);

  const slipAmount = useMemo(() => {
    const n = Number(paymentSlipForm.amountRial.replace(/,/g, ""));
    return isNaN(n) ? 0 : n;
  }, [paymentSlipForm.amountRial]);

  const unitCount = useMemo(() => {
    const n = Number(unitStr.replace(/,/g, ""));
    return isNaN(n) ? 0 : n;
  }, [unitStr]);

  const calculatedUnits =
    inputMode === "amount" && activeNav > 0
      ? Math.floor(amount / activeNav)
      : unitCount;

  const calculatedAmount =
    inputMode === "unit" ? unitCount * activeNav : amount;

  const redemptionAmount = unitCount * cancelNav;

  const effectiveUnits =
    investmentType === "subscription" ? calculatedUnits : unitCount;
  const effectiveAmount =
    investmentType === "subscription" ? calculatedAmount : redemptionAmount;

  const issuanceFixedWage = getWageSetting(fund, "IssuanceFixedWage");
  const redemptionFixedWage = getWageSetting(fund, "RedemptionFixedWage");
  const issuanceVariableWageRate = getWageSetting(
    fund,
    "IssuanceVariableWageRate",
  );
  const issuanceMaxVariableWage = getWageSetting(
    fund,
    "IssuanceMaxVariableWage",
  );
  const redemptionVariableWageRate = 0;
  const wageRate =
    investmentType === "subscription"
      ? issuanceVariableWageRate
      : redemptionVariableWageRate;
  const fixedWage =
    investmentType === "subscription" ? issuanceFixedWage : redemptionFixedWage;
  const variableWage = calculateVariableWage(
    effectiveAmount,
    wageRate,
    investmentType === "subscription" ? issuanceMaxVariableWage : 0,
  );
  const wageAmount = fixedWage + variableWage;
  const cardIssuanceBreakdown = calculateCardIssuanceBreakdown({
    cardAmountRial: Number(cardLookup?.amount || 0),
    unitPrice: activeNav,
    fixedWage: issuanceFixedWage,
    wageRate: issuanceVariableWageRate,
    maxVariableWage: issuanceMaxVariableWage,
  });
  const cardCalculatedUnits = cardIssuanceBreakdown.units;
  const cardWageAmount = cardIssuanceBreakdown.wageAmountRial;
  const cardNetInvestmentAmount = Math.max(
    0,
    Math.round(Number(cardLookup?.amount || 0) - cardWageAmount),
  );
  const gatewayFinalAmount =
    investmentType === "subscription"
      ? effectiveAmount + wageAmount
      : effectiveAmount - wageAmount;
  const finalAmount =
    investmentType === "subscription"
      ? paymentMethod === "card"
        ? cardLookup?.amount || 0
        : paymentMethod === "slip"
          ? slipAmount
          : gatewayFinalAmount
      : gatewayFinalAmount;
  const summaryUnits =
    investmentType === "subscription"
      ? paymentMethod === "card"
        ? cardCalculatedUnits
        : paymentMethod === "slip"
          ? floorWholeUnits(slipAmount, activeNav)
          : effectiveUnits
      : effectiveUnits;
  const summaryAmount =
    investmentType === "subscription"
      ? paymentMethod === "card"
        ? cardNetInvestmentAmount
        : paymentMethod === "slip"
          ? slipAmount
          : effectiveAmount
      : effectiveAmount;
  const displayedWageAmount =
    investmentType === "subscription" && paymentMethod === "card"
      ? cardWageAmount
      : wageAmount;
  const summaryUnitsLabel =
    investmentType === "subscription" && paymentMethod === "slip"
      ? "پس از بررسی"
      : `${formatNumber(summaryUnits)} واحد`;
  const minCapitalUnit = Number(fund?.minimumCapitalUnit || 0);
  const maxCapitalUnit = Number(fund?.maximumCapitalUnit || 0);
  const inputRangeHint =
    investmentType === "subscription"
      ? paymentMethod === "gateway" || paymentMethod === "card"
        ? isSepahFund
          ? `حداقل ${formatNumber(MIN_SEPAH_ISSUANCE_AMOUNT_RIAL)} ریال`
          : `حداقل ${formatNumber(MIN_GATEWAY_ISSUANCE_AMOUNT_RIAL)} — حداکثر ${formatNumber(MAX_GATEWAY_ISSUANCE_AMOUNT_RIAL)} ریال`
        : maxCapitalUnit > 0
          ? `حداقل ${formatNumber(minCapitalUnit)} — حداکثر ${formatNumber(maxCapitalUnit)} واحد`
          : `حداقل ${formatNumber(minCapitalUnit)} واحد`
      : "";
  const normalizedCardNumber = cardNumber.replace(/[^\d]/g, "");
  const isCardValidationPending =
    investmentType === "subscription" &&
    paymentMethod === "card" &&
    ((normalizedCardNumber.length > 0 && normalizedCardNumber.length < 16) ||
      isCardLookupLoading);
  const isCardMethodInvalid =
    investmentType === "subscription" &&
    paymentMethod === "card" &&
    (!cardLookup || Boolean(cardLookupError));
  const isSlipMethodInvalid =
    investmentType === "subscription" &&
    paymentMethod === "slip" &&
    (!slipImage ||
      !transferedAt ||
      slipAmount <= 0 ||
      !paymentSlipForm.sourceAccountNumber.trim() ||
      !paymentSlipForm.destinationAccountNumber.trim() ||
      !paymentSlipForm.sourceBankCode.trim() ||
      !paymentSlipForm.sourceBankName.trim() ||
      !paymentSlipForm.sourceBankId.trim());
  const hasSubscriptionDraftInput =
    paymentMethod === "card"
      ? normalizedCardNumber.length > 0
      : paymentMethod === "slip"
        ? slipAmount > 0
        : inputMode === "amount"
          ? amount > 0
          : unitCount > 0;
  const subscriptionValidationMessage =
    investmentType === "subscription"
      ? resolveSubscriptionValidationMessage({
          fund,
          units: paymentMethod === "card" ? cardCalculatedUnits : effectiveUnits,
          amountRial: Math.round(finalAmount),
          paymentMethod,
          isSepahFund,
        })
      : "";
  const redemptionValidationMessage =
    investmentType !== "subscription" && effectiveUnits <= 0
      ? "تعداد واحد ابطال باید معتبر باشد."
      : "";
  const inputValidationMessage =
    investmentType === "subscription"
      ? hasSubscriptionDraftInput
        ? subscriptionValidationMessage
        : ""
      : unitCount > 0
        ? redemptionValidationMessage
        : "";

  const resetForType = (type: InvestmentType) => {
    setInvestmentType(type);
    setInputMode(type === "subscription" ? "amount" : "unit");
    setPaymentMethod("gateway");
    setAmountStr("");
    setUnitStr("");
    setCardNumber("");
    setCardLookup(null);
    setCardLookupError("");
    setSlipImage(null);
    setTransferedAt(null);
    setPaymentSlipForm(initialPaymentSlipForm);
    setSelectedBankObjectId("");
  };

  useEffect(() => {
    if (investmentType !== "subscription" || paymentMethod !== "slip") {
      return;
    }

    let isMounted = true;
    const loadBanks = async () => {
      try {
        setIsBanksLoading(true);
        setBanksError("");
        const response = await banks.getAll(1, 500);
        if (isMounted) {
          setBankOptions(response.banks || []);
        }
      } catch (error: any) {
        if (isMounted) {
          setBanksError(
            error?.response?.data?.message ||
              "امکان دریافت لیست بانک‌ها وجود ندارد.",
          );
        }
      } finally {
        if (isMounted) {
          setIsBanksLoading(false);
        }
      }
    };

    if (bankOptions.length === 0) {
      void loadBanks();
    }

    return () => {
      isMounted = false;
    };
  }, [bankOptions.length, investmentType, paymentMethod]);

  useEffect(() => {
    if (investmentType !== "subscription" || paymentMethod !== "card") {
      setCardLookup(null);
      setCardLookupError("");
      setIsCardLookupLoading(false);
      return;
    }

    const normalizedCard = cardNumber.replace(/[^\d]/g, "");
    if (normalizedCard.length === 0) {
      setCardLookup(null);
      setCardLookupError("");
      return;
    }

    if (normalizedCard.length < 16) {
      setCardLookup(null);
      setCardLookupError("شماره کارت باید ۱۶ رقم باشد.");
      return;
    }

    let isMounted = true;
    const timer = window.setTimeout(async () => {
      try {
        setIsCardLookupLoading(true);
        setCardLookupError("");
        const selectedFundSeoRegisterNo = String(
          fund?.nav?.seoRegisterNo || fundId || fund?.fundRegisterCode || "",
        );
        const response = await paymentCards.lookup(
          normalizedCard,
          selectedFundSeoRegisterNo,
        );
        if (isMounted) {
          setCardLookup(response);
        }
      } catch (error: any) {
        if (isMounted) {
          setCardLookup(null);
          setCardLookupError(
            error?.response?.data?.message ||
              "امکان بررسی کارت هدیه وجود ندارد.",
          );
        }
      } finally {
        if (isMounted) {
          setIsCardLookupLoading(false);
        }
      }
    }, 300);

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [
    cardNumber,
    fund?.fundRegisterCode,
    fund?.nav?.seoRegisterNo,
    fundId,
    investmentType,
    paymentMethod,
  ]);

  useEffect(() => {
    const requestedMode = searchParams.get("mode");
    const nextType =
      requestedMode === "redemption" ? "redemption" : "subscription";
    resetForType(nextType);
  }, [searchParams]);

  useEffect(() => {
    const requestedMode = searchParams.get("mode");
    const requestedAmount = searchParams.get("amount");
    const nextType =
      requestedMode === "redemption" ? "redemption" : "subscription";

    if (nextType !== "subscription") {
      return;
    }

    if (isSepahFund) {
      setPaymentMethod("gateway");
      return;
    }

    const numericAmount = Number(
      String(requestedAmount || "")
        .replace(/[^\d]/g, "")
        .trim(),
    );

    if (Number.isFinite(numericAmount) && numericAmount > 0) {
      setInputMode("amount");
      setAmountStr(Math.floor(numericAmount).toLocaleString("en-US"));
    }
  }, [isSepahFund, searchParams]);

  if (isLoading || isUserLoading) {
    return <InvestmentSkeleton />;
  }

  if (fundId && isInvestmentDisabled) {
    return (
      <Container maxW="container.md" py={{ base: 10, md: 16 }}>
        <Box
          bg={cardBg}
          borderRadius="3xl"
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 6, md: 8 }}
          textAlign="center"
        >
          <VStack spacing={4}>
            <Heading size="md" color={textColor}>
              سرمایه‌گذاری برای این صندوق غیرفعال است
            </Heading>
            <Text color={subtitleColor} lineHeight="tall">
              در حال حاضر امکان ثبت درخواست سرمایه‌گذاری برای صندوق{" "}
              {fund?.fundName?.trim() || fundId} وجود ندارد.
            </Text>
            <Button
              colorScheme="brand"
              onClick={() => navigate(`/funds/${fundId}`)}
            >
              بازگشت به صفحه صندوق
            </Button>
          </VStack>
        </Box>
      </Container>
    );
  }

  if (requiresSejamSync) {
    return (
      <Flex py="30px" pt="80px" justify={"center"} align="center">
        <SejamSyncRequiredSection
          identifier={getUserIdentifier(identityUser)}
          identifierLabel={getUserIdentifierLabel(identityUser)}
          title="برای صدور و ابطال، همگام‌سازی سجام لازم است"
          description={
            requiresSejamSync
              ? "برای تکمیل ثبت‌نام تدبیر، لازم است یک‌بار دیگر اطلاعات سجام شما دریافت و ذخیره شود. بعد از همگام‌سازی، همین صفحه برای شما فعال می‌شود."
              : "تا زمانی که اطلاعات سجام شما تکمیل و در پروفایل ثبت نشده باشد، امکان ثبت درخواست صدور یا ابطال وجود ندارد. بعد از همگام‌سازی، همین صفحه برای شما فعال می‌شود."
          }
          minHeight="70vh"
          onSynced={async () => {
            await refetchCurrentUser();
          }}
        />
      </Flex>
    );
  }

  const hasInput =
    investmentType === "subscription"
      ? paymentMethod === "card"
        ? Boolean(cardLookup && !cardLookupError)
        : paymentMethod === "slip"
          ? !isSlipMethodInvalid
          : inputMode === "amount"
            ? amount > 0
            : unitCount > 0
      : unitCount > 0;
  const nextValidationMessage = !hasInput
    ? "لطفاً مقدار درخواست را وارد کنید."
    : inputValidationMessage;
  const isNextDisabled =
    isCardValidationPending || Boolean(nextValidationMessage);
  const submitValidationMessage = inputValidationMessage;
  const isSubmitDisabled =
    !agreed ||
    isCardValidationPending ||
    isCardMethodInvalid ||
    isSlipMethodInvalid ||
    Boolean(submitValidationMessage);

  // ── Handlers ──
  const handleTypeChange = (type: InvestmentType) => {
    resetForType(type);
  };

  const handleAmountChange = (val: string) => {
    const clean = val.replace(/[^\d]/g, "");
    setAmountStr(clean === "" ? "" : Number(clean).toLocaleString("en-US"));
  };

  const handlePaymentSlipChange = (
    field: keyof PaymentSlipForm,
    value: string,
  ) => {
    setPaymentSlipForm((current) => ({
      ...current,
      [field]:
        field === "amountRial"
          ? value.replace(/[^\d]/g, "") === ""
            ? ""
            : Number(value.replace(/[^\d]/g, "")).toLocaleString("en-US")
          : value,
    }));
  };

  const handleSlipBankChange = (bankObjectId: string) => {
    setSelectedBankObjectId(bankObjectId);

    const selectedBank = bankOptions.find(
      (bank: any) => String(bank._id) === bankObjectId,
    );

    setPaymentSlipForm((current) => ({
      ...current,
      sourceBankCode: selectedBank?.bankCode || "",
      sourceBankName: selectedBank?.name || "",
      sourceBankId: selectedBank?.sejamBankId || "",
    }));
  };

  const handleUnitChange = (val: string) => {
    const clean = val.replace(/[^\d]/g, "");
    setUnitStr(clean === "" ? "" : Number(clean).toLocaleString("en-US"));
  };

  const handleCalculatorSubmit = (
    _fundRegisterCode?: string,
    amountInRial?: number,
  ) => {
    if (Number.isFinite(amountInRial) && Number(amountInRial) > 0) {
      setInputMode("amount");
      setAmountStr(Math.floor(Number(amountInRial)).toLocaleString("en-US"));
    }
    calculatorModal.onClose();
  };

  const handleNext = () => {
    if (
      investmentType === "subscription" &&
      paymentMethod === "card" &&
      (!cardLookup || cardLookupError)
    ) {
      toast({
        title: "کارت هدیه معتبر نیست",
        description:
          cardLookupError || "ابتدا یک کارت هدیه فعال و معتبر وارد کنید.",
        status: "warning",
        duration: 2500,
        position: "top",
        isClosable: true,
      });
      return;
    }

    if (
      investmentType === "subscription" &&
      paymentMethod === "slip" &&
      isSlipMethodInvalid
    ) {
      toast({
        title: "اطلاعات فیش انتقال کامل نیست",
        description:
          "تصویر فیش، تاریخ انتقال، مبلغ و اطلاعات حساب مبدا را کامل کنید.",
        status: "warning",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    if (nextValidationMessage) {
      toast({
        title: "امکان ادامه وجود ندارد",
        description: nextValidationMessage,
        status: "warning",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setStep(1);
    setAgreed(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!fund) {
      return;
    }

    if (!agreed) {
      toast({
        title: "تأیید قوانین الزامی است",
        status: "warning",
        duration: 2500,
        position: "top",
        isClosable: true,
      });
      return;
    }

    const resolvedFundKey = resolveEasyPurchaseFundKey(fund.fundRegisterCode);
    const fundKey = resolvedFundKey || fund.fundRegisterCode;

    if (
      investmentType === "subscription" &&
      !isSepahFund &&
      paymentMethod !== "slip" &&
      !resolvedFundKey
    ) {
      toast({
        title: "درگاه این صندوق تنظیم نشده است",
        description: "برای این صندوق پذیرنده پرداخت تعریف نشده است.",
        status: "error",
        duration: 4000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    if (investmentType !== "subscription" && effectiveUnits <= 0) {
      toast({
        title: "تعداد واحد ابطال معتبر نیست",
        status: "warning",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    if (paymentMethod === "card" && !cardLookup) {
      toast({
        title: "کارت هدیه معتبر نیست",
        description: "ابتدا شماره کارت معتبر را بررسی کنید.",
        status: "warning",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    if (paymentMethod === "slip" && isSlipMethodInvalid) {
      toast({
        title: "اطلاعات فیش انتقال کامل نیست",
        description: "لطفاً همه فیلدهای روش انتقال با فیش را کامل کنید.",
        status: "warning",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    const amountRial =
      paymentMethod === "card"
        ? Number(cardLookup?.amount || 0)
        : paymentMethod === "slip"
          ? Math.round(slipAmount)
          : Math.round(finalAmount);
    const units =
      paymentMethod === "card"
        ? cardCalculatedUnits
        : paymentMethod === "slip"
          ? 0
          : Number(effectiveUnits);

    if (submitValidationMessage) {
      toast({
        title: "امکان ثبت درخواست وجود ندارد",
        description: submitValidationMessage,
        status: "warning",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      if (investmentType === "redemption") {
        const response = await requests.createRedemption({
          fundKey,
          fundId: fund.fundRegisterCode,
          fundSeoRegisterNo: String(
            fund.nav?.seoRegisterNo || fund.fundRegisterCode,
          ),
          fundName: fund.fundName.trim(),
          units: Number(effectiveUnits),
          unitPrice: cancelNav,
          amountRial: Math.round(finalAmount),
          notes: "درخواست ابطال ثبت‌شده از صفحه سرمایه‌گذاری",
          meta: {
            source: "fund-invest",
            inputMode,
            investmentType,
            grossAmountRial: Math.round(redemptionAmount),
            wageAmount: Math.round(wageAmount),
            fixedWage: Math.round(fixedWage),
            variableWage: Math.round(variableWage),
            fundRegisterCode: fund.fundRegisterCode,
          },
        });

        toast({
          title: "درخواست ابطال ثبت شد",
          description:
            response.message ||
            "درخواست شما با موفقیت ثبت شد و در صف بررسی قرار گرفت.",
          status: "success",
          duration: 4000,
          position: "top",
          isClosable: true,
        });
        navigate("/dashboard/requests");
        return;
      }

      if (paymentMethod === "slip") {
        if (!slipImage || !transferedAt) return;

        const uploaded = await files.upload(slipImage);
        const response = await paymentSlips.create({
          receiptFile: uploaded.file._id,
          fundSeoRegisterNo: String(
            fund.nav?.seoRegisterNo || fundId || fund.fundRegisterCode,
          ),
          fundName: fund.fundName.trim(),
          sourceAccountNumber: paymentSlipForm.sourceAccountNumber.trim(),
          destinationAccountNumber:
            paymentSlipForm.destinationAccountNumber.trim(),
          sourceBankCode: paymentSlipForm.sourceBankCode.trim(),
          sourceBankName: paymentSlipForm.sourceBankName.trim(),
          sourceBankId: paymentSlipForm.sourceBankId.trim(),
          transferedAt: toGregorianIsoDate(transferedAt),
          amountRial,
          notes: "درخواست صدور ثبت‌شده با فیش انتقال از صفحه سرمایه‌گذاری",
        });

        toast({
          title: "فیش انتقال ثبت شد",
          description:
            response.message ||
            "درخواست شما با موفقیت ثبت شد و در صف بررسی قرار گرفت.",
          status: "success",
          duration: 4000,
          position: "top",
          isClosable: true,
        });
        navigate(
          `/payment-result/${encodeURIComponent(response?.request?._id || "")}`,
        );
        return;
      }

      const response = await easyPurchase.startEasyPurchase(
        fund.fundRegisterCode,
        {
          confirm: true,
          client: "web",
          fundKey,
          fundName: fund.fundName.trim(),
          fundSeoRegisterNo: fund.nav?.seoRegisterNo || fund.fundRegisterCode,
          paymentMethod,
          cardNumber: paymentMethod === "card" ? cardNumber : undefined,
          amountRial,
          units,
          unitPrice: activeNav,
          meta: {
            source: "fund-invest",
            paymentMethod,
            inputMode,
            fundRegisterCode: fund.fundRegisterCode,
            wageAmount:
              paymentMethod === "card"
                ? Math.round(cardWageAmount)
                : Math.round(wageAmount),
            fixedWage:
              paymentMethod === "card"
                ? Math.round(issuanceFixedWage)
                : Math.round(fixedWage),
            variableWage:
              paymentMethod === "card"
                ? Math.max(0, Math.round(cardWageAmount - issuanceFixedWage))
                : Math.round(variableWage),
            cardAmount:
              paymentMethod === "card" ? cardLookup?.amount : undefined,
          },
        },
      );

      window.location.assign(response.redirectUrl);
    } catch (error: any) {
      toast({
        title:
          investmentType === "subscription"
            ? "امکان شروع پرداخت وجود ندارد"
            : "امکان ثبت درخواست ابطال وجود ندارد",
        description:
          error.response?.data?.message ||
          "لطفاً چند لحظه دیگر دوباره تلاش کنید.",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── 404 ──
  if (!fund) {
    return (
      <Box py={40} textAlign="center">
        <Container maxW="container.sm">
          <VStack spacing={5}>
            <Heading color={textColor} fontSize="xl">
              صندوق مورد نظر یافت نشد
            </Heading>
            <Button
              colorScheme="brand"
              onClick={() => navigate("/funds")}
              rightIcon={
                <Icon verticalAlign="middle" as={FiArrowLeft as any} />
              }
            >
              بازگشت به لیست صندوق‌ها
            </Button>
          </VStack>
        </Container>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────
  return (
    <Box bg={sectionBg} minH="100vh" pt={{ base: "100px", md: "90px" }} pb={16}>
      <Container maxW="container.sm">
        <Flex flexDir={"column"}>
          <InvestmentHeader
            activeStep={step}
            borderColor={borderColor}
            cancelNav={cancelNav}
            cardBg={cardBg}
            fund={fund}
            fundLogoSrc={fundLogoSrc}
            heroBgGradient={heroBgGradient}
            investorTotalAsset={investorTotalAsset}
            investorTotalUnits={investorTotalUnits}
            isInvestorProfileLoading={isInvestorProfileLoading}
            onBackToDetails={handleBack}
            stepActiveBg={stepActiveBg}
            stepDoneBg={stepDoneBg}
            stepInactiveBg={stepInactiveBg}
            steps={INVESTMENT_STEPS}
            subscriptionNav={subscriptionNav}
            subtitleColor={subtitleColor}
            textColor={textColor}
          />

          {canManageDividend ? (
            <Button
              alignSelf="stretch"
              mb={4}
              borderRadius="xl"
              variant="outline"
              rightIcon={<Icon as={FiPercent as any} />}
              onClick={reinvestModal.onOpen}
            >
              مدیریت سود
            </Button>
          ) : null}

          {canUseCalculator && investmentType === "subscription" ? (
            <Button
              alignSelf="stretch"
              mb={4}
              borderRadius="xl"
              variant="outline"
              rightIcon={<Icon as={FiPieChart as any} />}
              onClick={() => {
                setCalculatorAmount("");
                setCalculatorInputMode("amount");
                calculatorModal.onOpen();
              }}
            >
              ماشین حساب
            </Button>
          ) : null}

          {/* ── Steps content ── */}
          <AnimatePresence mode="wait">
            {step === 1 && (
              <MotionBox
                key="step1"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
              >
                <VStack spacing={4} align="stretch">
                  <Box
                    bg={cardBg}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={borderColor}
                    p={5}
                  >
                    <VStack spacing={5} align="stretch">
                      {/* ── صدور / ابطال toggle ── */}
                      <Box
                        bg={sectionBg}
                        borderRadius="xl"
                        p="5px"
                        display="grid"
                        gridTemplateColumns="1fr 1fr"
                        gap="4px"
                      >
                        {(
                          [
                            {
                              type: "subscription" as InvestmentType,
                              label: "صدور (خرید)",
                              icon: FiTrendingUp,
                            },
                            {
                              type: "redemption" as InvestmentType,
                              label: "ابطال (فروش)",
                              icon: FiTrendingDown,
                            },
                          ] as const
                        ).map(({ type, label, icon }) => {
                          const active = investmentType === type;
                          return (
                            <Box
                              key={type}
                              onClick={() => handleTypeChange(type)}
                              cursor="pointer"
                              borderRadius="lg"
                              px={3}
                              py={2.5}
                              bg={active ? typeActiveBg : "transparent"}
                              border="1.5px solid"
                              borderColor={
                                active ? typeActiveBorder : "transparent"
                              }
                              transition="all 0.2s"
                              _hover={!active ? { bg: modeHoverBg } : {}}
                            >
                              <HStack justify="center" spacing={2}>
                                <Icon
                                  verticalAlign="middle"
                                  as={icon as any}
                                  boxSize={4}
                                  color={
                                    active ? typeActiveText : typeInactiveText
                                  }
                                />
                                <Text
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color={
                                    active ? typeActiveText : typeInactiveText
                                  }
                                >
                                  {label}
                                </Text>
                              </HStack>
                            </Box>
                          );
                        })}
                      </Box>

                      {/* ── Payment method (صدور only) ── */}
                      {investmentType === "subscription" && (
                        <VStack spacing={2} align="stretch">
                          {isSepahFund ? (
                            <Text
                              fontSize="xs"
                              color={subtitleColor}
                              textAlign="center"
                            >
                              برای این صندوق فقط پرداخت از طریق درگاه آنلاین
                              فعال است.
                            </Text>
                          ) : null}
                          <HStack
                            spacing={0}
                            borderRadius="lg"
                            overflow="hidden"
                            border="1px solid"
                            borderColor={borderColor}
                          >
                            {(
                              [
                                {
                                  mode: "gateway" as PaymentMethod,
                                  label: "درگاه پرداخت",
                                },
                                ...(!isSepahFund
                                  ? ([
                                      {
                                        mode: "card" as PaymentMethod,
                                        label: "کارت هدیه",
                                      },
                                      {
                                        mode: "slip" as PaymentMethod,
                                        label: "فیش انتقال",
                                      },
                                    ] as const)
                                  : []),
                              ] as Array<{
                                mode: PaymentMethod;
                                label: string;
                              }>
                            ).map(({ mode, label }) => {
                              const active = paymentMethod === mode;
                              return (
                                <Box
                                  key={mode}
                                  flex={1}
                                  textAlign="center"
                                  py={2}
                                  px={3}
                                  cursor="pointer"
                                  bg={active ? modeActiveBg : "transparent"}
                                  borderRight={
                                    mode !== "gateway" ? "1px solid" : "none"
                                  }
                                  borderColor={borderColor}
                                  onClick={() => {
                                    setPaymentMethod(mode);
                                    setAmountStr("");
                                    setUnitStr("");
                                    setCardNumber("");
                                    setCardLookup(null);
                                    setCardLookupError("");
                                    setSlipImage(null);
                                  }}
                                  transition="all 0.15s"
                                  _hover={!active ? { bg: modeHoverBg } : {}}
                                >
                                  <Text
                                    fontSize="xs"
                                    fontWeight={active ? "bold" : "normal"}
                                    color={
                                      active ? modeActiveText : subtitleColor
                                    }
                                  >
                                    {label}
                                  </Text>
                                </Box>
                              );
                            })}
                          </HStack>
                        </VStack>
                      )}

                      {/* ── Input mode (gateway only) ── */}
                      {investmentType === "subscription" &&
                        paymentMethod === "gateway" && (
                          <HStack
                            spacing={0}
                            borderRadius="lg"
                            overflow="hidden"
                            border="1px solid"
                            borderColor={borderColor}
                          >
                            {(
                              [
                                {
                                  mode: "amount" as InputMode,
                                  label: "بر اساس مبلغ",
                                },
                                {
                                  mode: "unit" as InputMode,
                                  label: "بر اساس تعداد واحد",
                                },
                              ] as const
                            ).map(({ mode, label }) => {
                              const active = inputMode === mode;
                              return (
                                <Box
                                  key={mode}
                                  flex={1}
                                  textAlign="center"
                                  py={2}
                                  px={3}
                                  cursor="pointer"
                                  bg={active ? modeActiveBg : "transparent"}
                                  borderRight={
                                    mode === "unit" ? "1px solid" : "none"
                                  }
                                  borderColor={borderColor}
                                  onClick={() => {
                                    setInputMode(mode);
                                    setAmountStr("");
                                    setUnitStr("");
                                  }}
                                  transition="all 0.15s"
                                  _hover={!active ? { bg: modeHoverBg } : {}}
                                >
                                  <Text
                                    fontSize="xs"
                                    fontWeight={active ? "bold" : "normal"}
                                    color={
                                      active ? modeActiveText : subtitleColor
                                    }
                                  >
                                    {label}
                                  </Text>
                                </Box>
                              );
                            })}
                          </HStack>
                        )}

                      {/* ── Main input ── */}
                      {investmentType === "subscription" &&
                      paymentMethod === "card" ? (
                        <VStack spacing={2} align="stretch">
                          <Text
                            fontSize="sm"
                            color={subtitleColor}
                            fontWeight="medium"
                          >
                            شماره کارت هدیه
                          </Text>
                          <InputGroup size="lg">
                            <Input
                              placeholder="شماره ۱۶ رقمی کارت"
                              value={formatGiftCardNumber(cardNumber)}
                              onChange={(e) =>
                                setCardNumber(
                                  e.target.value
                                    .replace(/[^\d]/g, "")
                                    .slice(0, 16),
                                )
                              }
                              textAlign="center"
                              fontSize="xl"
                              fontWeight="bold"
                              borderColor={borderColor}
                              borderRadius="xl"
                              letterSpacing="0.12em"
                              _focus={{
                                borderColor: "brand.500",
                                boxShadow:
                                  "0 0 0 1px var(--chakra-colors-brand-500)",
                              }}
                              h="60px"
                            />
                          </InputGroup>
                          <Text
                            fontSize="xs"
                            color={cardLookupError ? "red.400" : subtitleColor}
                            textAlign="center"
                          >
                            {isCardLookupLoading
                              ? "در حال بررسی کارت هدیه..."
                              : cardLookup
                                ? `مبلغ کارت هدیه ${formatNumber(cardLookup.amount)} ریال و تاریخ انقضا ${new Date(cardLookup.expiresAt).toLocaleDateString("fa-IR")} است.`
                                : cardLookupError ||
                                  "با این روش، مبلغ پرداخت باید دقیقاً برابر مبلغ کارت باشد."}
                          </Text>
                        </VStack>
                      ) : investmentType === "subscription" &&
                        paymentMethod === "slip" ? (
                        <VStack spacing={4} align="stretch">
                          <Box
                            border="1px dashed"
                            borderColor={slipImage ? "green.300" : borderColor}
                            borderRadius="xl"
                            p={4}
                            bg={sectionBg}
                          >
                            <VStack spacing={3}>
                              <Icon
                                verticalAlign="middle"
                                as={FiUploadCloud as any}
                                boxSize={6}
                                color={slipImage ? "green.500" : "brand.500"}
                              />
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={textColor}
                              >
                                {slipImage
                                  ? slipImage.name
                                  : "تصویر فیش انتقال را بارگذاری کنید"}
                              </Text>
                              <Button
                                as="label"
                                htmlFor="slip-transfer-image"
                                size="sm"
                                colorScheme="brand"
                                variant={slipImage ? "outline" : "solid"}
                                cursor="pointer"
                              >
                                انتخاب تصویر
                              </Button>
                              <Input
                                id="slip-transfer-image"
                                type="file"
                                accept="image/*"
                                display="none"
                                onChange={(event) =>
                                  setSlipImage(event.target.files?.[0] || null)
                                }
                              />
                            </VStack>
                          </Box>

                          <SimpleGrid columns={1} spacing={3}>
                            <FormControl>
                              <FormLabel fontSize="xs" color={subtitleColor}>
                                بانک مبدا
                              </FormLabel>
                              <Select
                                placeholder={
                                  isBanksLoading
                                    ? "در حال دریافت بانک‌ها..."
                                    : "بانک را انتخاب کنید"
                                }
                                value={selectedBankObjectId}
                                onChange={(event) =>
                                  handleSlipBankChange(event.target.value)
                                }
                                textAlign="center"
                                isDisabled={isBanksLoading}
                                borderColor={borderColor}
                                borderRadius="xl"
                              >
                                {bankOptions.map((bank: any) => (
                                  <option key={bank._id} value={bank._id}>
                                    {bank.name}
                                  </option>
                                ))}
                              </Select>
                              {banksError ? (
                                <Text fontSize="xs" color="red.400" mt={1}>
                                  {banksError}
                                </Text>
                              ) : null}
                            </FormControl>
                            <SimpleGrid
                              columns={{ base: 1, md: 2 }}
                              spacing={3}
                            >
                              <FormControl>
                                <FormLabel fontSize="xs" color={subtitleColor}>
                                  شماره حساب مبدا
                                </FormLabel>
                                <Input
                                  value={paymentSlipForm.sourceAccountNumber}
                                  placeholder="شماره حساب مبدا"
                                  onChange={(event) =>
                                    handlePaymentSlipChange(
                                      "sourceAccountNumber",
                                      event.target.value,
                                    )
                                  }
                                  textAlign="center"
                                  borderColor={borderColor}
                                  borderRadius="xl"
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="xs" color={subtitleColor}>
                                  شماره حساب مقصد
                                </FormLabel>
                                <Input
                                  value={
                                    paymentSlipForm.destinationAccountNumber
                                  }
                                  placeholder="شماره حساب مقصد"
                                  onChange={(event) =>
                                    handlePaymentSlipChange(
                                      "destinationAccountNumber",
                                      event.target.value,
                                    )
                                  }
                                  textAlign="center"
                                  borderColor={borderColor}
                                  borderRadius="xl"
                                />
                              </FormControl>
                            </SimpleGrid>
                            <FormControl>
                              <FormLabel fontSize="xs" color={subtitleColor}>
                                تاریخ انتقال
                              </FormLabel>
                              <PersianDatePicker
                                value={transferedAt}
                                onChange={setTransferedAt}
                                placeholder="تاریخ انتقال"
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel fontSize="xs" color={subtitleColor}>
                                مبلغ انتقال (ریال)
                              </FormLabel>
                              <Input
                                value={paymentSlipForm.amountRial}
                                placeholder="مبلغ انتقال (ریال)"
                                onChange={(event) =>
                                  handlePaymentSlipChange(
                                    "amountRial",
                                    event.target.value,
                                  )
                                }
                                textAlign="center"
                                fontWeight="bold"
                                borderColor={borderColor}
                                borderRadius="xl"
                              />
                            </FormControl>
                          </SimpleGrid>
                        </VStack>
                      ) : (
                        <VStack spacing={2} align="stretch">
                          <Text
                            fontSize="sm"
                            color={subtitleColor}
                            fontWeight="medium"
                          >
                            {investmentType === "subscription"
                              ? inputMode === "amount"
                                ? "مبلغ سرمایه‌گذاری (ریال)"
                                : "تعداد واحد"
                              : "تعداد واحد ابطال"}
                          </Text>
                          <InputGroup size="lg">
                            <Input
                              placeholder="0"
                              value={
                                investmentType === "subscription"
                                  ? inputMode === "amount"
                                    ? amountStr
                                    : unitStr
                                  : unitStr
                              }
                              onChange={(e) => {
                                if (
                                  investmentType === "subscription" &&
                                  inputMode === "amount"
                                ) {
                                  handleAmountChange(e.target.value);
                                } else {
                                  handleUnitChange(e.target.value);
                                }
                              }}
                              textAlign="center"
                              fontSize="2xl"
                              fontWeight="bold"
                              borderColor={borderColor}
                              borderRadius="xl"
                              _focus={{
                                borderColor: "brand.500",
                                boxShadow:
                                  "0 0 0 1px var(--chakra-colors-brand-500)",
                              }}
                              h="60px"
                            />
                            <InputLeftAddon
                              bg={sectionBg}
                              borderColor={borderColor}
                              fontSize="sm"
                              color={subtitleColor}
                              h="60px"
                            >
                              {investmentType === "subscription"
                                ? inputMode === "amount"
                                  ? "ریال"
                                  : "واحد"
                                : "واحد"}
                            </InputLeftAddon>
                          </InputGroup>

                          {investmentType === "subscription" && (
                            <VStack spacing={1} align="stretch">
                              <Text
                                fontSize="xs"
                                color={subtitleColor}
                                textAlign="center"
                              >
                                {inputRangeHint}
                              </Text>
                              {inputValidationMessage ? (
                                <Text
                                  fontSize="xs"
                                  color="red.500"
                                  textAlign="center"
                                >
                                  {inputValidationMessage}
                                </Text>
                              ) : null}
                            </VStack>
                          )}
                        </VStack>
                      )}

                      {/* ── Live calculation ── */}
                      <AnimatePresence>
                        {hasInput && paymentMethod !== "slip" && (
                          <MotionBox
                            key="calc"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            overflow="hidden"
                          >
                            <Box bg={calcBg} borderRadius="xl" p={4}>
                              <VStack spacing={2} align="stretch">
                                {investmentType === "subscription" ? (
                                  <>
                                    {paymentMethod === "card" ? (
                                      <>
                                        <HStack justify="space-between">
                                          <Text
                                            fontSize="sm"
                                            color={subtitleColor}
                                          >
                                            مبلغ کارت
                                          </Text>
                                          <Text
                                            fontSize="sm"
                                            fontWeight="bold"
                                            color={textColor}
                                          >
                                            {formatNumber(
                                              cardLookup?.amount || 0,
                                            )}{" "}
                                            ریال
                                          </Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                          <Text
                                            fontSize="sm"
                                            color={subtitleColor}
                                          >
                                            مبلغ سرمایه گذاری
                                          </Text>
                                          <Text
                                            fontSize="sm"
                                            fontWeight="bold"
                                            color={textColor}
                                          >
                                            {formatNumber(
                                              cardNetInvestmentAmount,
                                            )}{" "}
                                            ریال
                                          </Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                          <Text
                                            fontSize="sm"
                                            color={subtitleColor}
                                          >
                                            تعداد واحد قابل صدور
                                          </Text>
                                          <Text
                                            fontSize="sm"
                                            fontWeight="bold"
                                            color={textColor}
                                          >
                                            {formatNumber(cardCalculatedUnits)}{" "}
                                            واحد
                                          </Text>
                                        </HStack>
                                        <Divider borderColor={calcDivider} />
                                        <HStack justify="space-between">
                                          <Text
                                            fontSize="sm"
                                            fontWeight="bold"
                                            color={textColor}
                                          >
                                            تعداد واحد قابل صدور
                                          </Text>
                                          <Text
                                            fontSize="md"
                                            fontWeight="extrabold"
                                            color={actionAccentColor}
                                          >
                                            {formatNumber(cardCalculatedUnits)}{" "}
                                            واحد
                                          </Text>
                                        </HStack>
                                      </>
                                    ) : inputMode === "amount" ? (
                                      <HStack justify="space-between">
                                        <Text
                                          fontSize="sm"
                                          color={subtitleColor}
                                        >
                                          تعداد واحد دریافتی
                                        </Text>
                                        <Text
                                          fontSize="sm"
                                          fontWeight="bold"
                                          color={textColor}
                                        >
                                          {formatNumber(calculatedUnits)} واحد
                                        </Text>
                                      </HStack>
                                    ) : (
                                      <HStack justify="space-between">
                                        <Text
                                          fontSize="sm"
                                          color={subtitleColor}
                                        >
                                          مبلغ قابل پرداخت
                                        </Text>
                                        <Text
                                          fontSize="sm"
                                          fontWeight="bold"
                                          color={textColor}
                                        >
                                          {formatNumber(calculatedAmount)} ریال
                                        </Text>
                                      </HStack>
                                    )}
                                    {paymentMethod === "gateway" && (
                                      <>
                                        <HStack justify="space-between">
                                          <Text
                                            fontSize="sm"
                                            color={subtitleColor}
                                          >
                                            کارمزد صدور
                                          </Text>
                                          <Text
                                            fontSize="sm"
                                            color={subtitleColor}
                                          >
                                            {formatNumber(
                                              Math.round(wageAmount),
                                            )}{" "}
                                            ریال
                                          </Text>
                                        </HStack>
                                        <Divider borderColor={calcDivider} />
                                        <HStack justify="space-between">
                                          <Text
                                            fontSize="sm"
                                            fontWeight="bold"
                                            color={textColor}
                                          >
                                            مبلغ نهایی
                                          </Text>
                                          <Text
                                            fontSize="md"
                                            fontWeight="extrabold"
                                            color={actionAccentColor}
                                          >
                                            {formatNumber(
                                              Math.round(finalAmount),
                                            )}{" "}
                                            ریال
                                          </Text>
                                        </HStack>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <HStack justify="space-between">
                                      <Text fontSize="sm" color={subtitleColor}>
                                        مبلغ تقریبی
                                      </Text>
                                      <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color={textColor}
                                      >
                                        {formatNumber(redemptionAmount)} ریال
                                      </Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                      <Text fontSize="sm" color={subtitleColor}>
                                        کارمزد ابطال
                                      </Text>
                                      <Text fontSize="sm" color={subtitleColor}>
                                        {formatNumber(Math.round(wageAmount))}{" "}
                                        ریال
                                      </Text>
                                    </HStack>
                                    <Divider borderColor={calcDivider} />
                                    <HStack justify="space-between">
                                      <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color={textColor}
                                      >
                                        مبلغ دریافتی
                                      </Text>
                                      <Text
                                        fontSize="md"
                                        fontWeight="extrabold"
                                        color={actionAccentColor}
                                      >
                                        {formatNumber(Math.round(finalAmount))}{" "}
                                        ریال
                                      </Text>
                                    </HStack>
                                  </>
                                )}
                              </VStack>
                            </Box>
                          </MotionBox>
                        )}
                      </AnimatePresence>
                    </VStack>
                  </Box>

                  <Button
                    colorScheme={actionColorScheme}
                    size="lg"
                    w="100%"
                    rightIcon={
                      <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                    }
                    onClick={handleNext}
                    isDisabled={isNextDisabled}
                    borderRadius="xl"
                    h="56px"
                    fontSize="md"
                    bg={
                      investmentType === "subscription"
                        ? "green.500"
                        : undefined
                    }
                    color={
                      investmentType === "subscription"
                        ? "white"
                        : undefined
                    }
                    _hover={
                      investmentType === "subscription"
                        ? { bg: "green.600" }
                        : undefined
                    }
                  >
                    {investmentType === "subscription"
                      ? "ادامه و بررسی صدور"
                      : "ادامه و بررسی ابطال"}
                  </Button>
                </VStack>
              </MotionBox>
            )}

            {step === 2 && (
              <MotionBox
                key="step2"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
              >
                <VStack spacing={4} align="stretch">
                  {/* Summary */}
                  <Box
                    bg={cardBg}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={borderColor}
                    p={5}
                  >
                    <VStack spacing={1} align="stretch">
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={subtitleColor}
                        mb={2}
                      >
                        خلاصه درخواست
                      </Text>
                      <InfoRow
                        label="نوع عملیات"
                        value={
                          investmentType === "subscription"
                            ? "صدور (خرید)"
                            : "ابطال (فروش)"
                        }
                        highlight
                      />
                      <InfoRow
                        label={
                          investmentType === "subscription"
                            ? getFundSubscriptionNavLabel(
                                fund?.fundRegisterCode,
                              )
                            : getFundRedemptionNavLabel(
                                fund?.fundRegisterCode,
                              )
                        }
                        value={`${formatNumber(activeNav)} ریال`}
                      />
                      <Divider borderColor={borderColor} my={1} />
                      <InfoRow
                        label="تعداد واحد"
                        value={summaryUnitsLabel}
                      />
                      <InfoRow
                        label={
                          investmentType === "subscription"
                            ? "مبلغ سرمایه‌گذاری"
                            : "مبلغ قبل از کارمزد"
                        }
                        value={`${formatNumber(summaryAmount)} ریال`}
                      />
                      {!(
                        investmentType === "subscription" &&
                        paymentMethod === "slip"
                      ) && (
                        <InfoRow
                          label={`کارمزد ${investmentType === "subscription" ? "صدور" : "ابطال"}`}
                          value={`${formatNumber(Math.round(displayedWageAmount))} ریال`}
                        />
                      )}
                      {investmentType === "subscription" && (
                        <InfoRow
                          label="روش پرداخت"
                          value={
                            paymentMethod === "card"
                              ? "کارت هدیه"
                              : paymentMethod === "slip"
                                ? "فیش انتقال"
                                : "درگاه پرداخت"
                          }
                        />
                      )}
                      {investmentType === "subscription" &&
                        paymentMethod === "slip" && (
                          <>
                            <InfoRow
                              label="تاریخ انتقال"
                              value={formatPersianDayValue(transferedAt)}
                            />
                            <InfoRow
                              label="بانک مبدا"
                              value={
                                paymentSlipForm.sourceBankCode || "ثبت نشده"
                              }
                            />
                          </>
                        )}
                      <Divider borderColor={borderColor} my={1} />
                      <InfoRow
                        label={
                          investmentType === "subscription"
                            ? "مبلغ نهایی قابل پرداخت"
                            : "مبلغ نهایی دریافتی"
                        }
                        value={`${formatNumber(Math.round(finalAmount))} ریال`}
                        highlight
                        large
                      />
                    </VStack>
                  </Box>

                  <InvestmentAgreement
                    agreed={agreed}
                    agreementOpen={agreementOpen}
                    borderColor={borderColor}
                    cardBg={cardBg}
                    subtitleColor={subtitleColor}
                    textColor={textColor}
                    setAgreed={setAgreed}
                    setAgreementOpen={setAgreementOpen}
                  />

                  {/* Buttons */}
                  <VStack spacing={3} align="stretch">
                    <Button
                      colorScheme={actionColorScheme}
                      size="lg"
                      w="100%"
                      rightIcon={
                        <Icon verticalAlign="middle" as={FiCheck as any} />
                      }
                      onClick={handleSubmit}
                      isDisabled={isSubmitDisabled}
                      isLoading={isSubmitting}
                      loadingText={
                        investmentType === "redemption"
                          ? "در حال انجام عملیات"
                          : paymentMethod === "card"
                            ? "در حال ثبت درخواست"
                            : paymentMethod === "slip"
                              ? "در حال ثبت فیش"
                              : "در حال اتصال به درگاه"
                      }
                      borderRadius="xl"
                      h="56px"
                      fontSize="md"
                      transition="all 0.25s"
                      _hover={
                        agreed
                          ? { transform: "translateY(-2px)", shadow: "lg" }
                          : {}
                      }
                    >
                      ثبت درخواست
                    </Button>
                    <Button
                      variant="outline"
                      colorScheme="gray"
                      size="lg"
                      w="100%"
                      rightIcon={
                        <Icon verticalAlign="middle" as={FiArrowRight as any} />
                      }
                      onClick={handleBack}
                      borderRadius="xl"
                    >
                      بازگشت و ویرایش
                    </Button>
                  </VStack>
                </VStack>
              </MotionBox>
            )}
          </AnimatePresence>
        </Flex>
      </Container>

      <Modal
        isOpen={Boolean(requiresSepahRegistration)}
        onClose={() => navigate("/funds")}
        isCentered
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent mx={4} borderRadius="2xl">
          <ModalHeader>ثبت نام در کارگزاری سپه</ModalHeader>
          <ModalCloseButton left={4} right="auto" />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text lineHeight="tall">
                جهت ثبت نام در کارگزاری سپه میبایست از طریق لینک زیر ثبت نام
                نمایید
              </Text>
              {sepahRegisterUrl ? (
                <Link
                  href={sepahRegisterUrl}
                  isExternal
                  color="brand.500"
                  fontWeight="bold"
                  wordBreak="break-all"
                >
                  {sepahRegisterUrl}
                </Link>
              ) : (
                <Text color="red.500">
                  لینک ثبت نام کارگزاری سپه تنظیم نشده است.
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => navigate("/funds")}>
              بستن
            </Button>
            {sepahRegisterUrl ? (
              <Button
                as="a"
                href={sepahRegisterUrl}
                target="_blank"
                rel="noopener noreferrer"
                colorScheme="brand"
              >
                ثبت نام
              </Button>
            ) : null}
          </ModalFooter>
        </ModalContent>
      </Modal>
      {canUseCalculator ? (
        <FundCalculatorModal
          isOpen={calculatorModal.isOpen}
          onClose={calculatorModal.onClose}
          fund={fund || null}
          amount={calculatorAmount}
          inputMode={calculatorInputMode}
          onAmountChange={setCalculatorAmount}
          onInputModeChange={setCalculatorInputMode}
          onSubmit={handleCalculatorSubmit}
        />
      ) : null}
      <ReinvestModal
        isOpen={reinvestModal.isOpen}
        onClose={reinvestModal.onClose}
        fundProfile={(investorFund || null) as ProfileFundCardEntry | null}
        onSubmitted={async () => {
          await refetchInvestorProfile();
        }}
      />
    </Box>
  );
};
